/**
 * Which chart primitives Clean hides, as a predicate for the primitive registry.
 *
 * Primitives are classified by class, not by the ref that happens to hold them.
 * Refs were the old approach and they are why Clean leaked: several owners
 * attach first and assign the ref afterwards, and owners recreate their
 * primitive on symbol switch, so at the moment of attachment the ref either
 * pointed at nothing or at the previous instance. A class is known the instant
 * the primitive exists.
 *
 * The predicate reads the Clean store at call time rather than closing over a
 * snapshot, so a primitive attached while Clean is on is suppressed on the
 * spot, with no effect ordering to get wrong.
 */
import { isCleanHidden, useCleanChartStore, type CleanChartState, type CleanTarget } from '../store/cleanChartStore';
import type { PrimitiveSuppressor } from './primitiveRegistry';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type PrimitiveClass = abstract new (...args: any[]) => object;

export type CleanGroupMap = ReadonlyMap<PrimitiveClass, CleanTarget>;

/** Build a class -> group map from a readable group -> classes table. */
export function cleanGroupMap(groups: Partial<Record<CleanTarget, PrimitiveClass[]>>): CleanGroupMap {
  const map = new Map<PrimitiveClass, CleanTarget>();
  for (const [group, classes] of Object.entries(groups) as [CleanTarget, PrimitiveClass[]][]) {
    for (const cls of classes) {
      if (map.has(cls)) throw new Error(`cleanGroupMap: ${cls.name} is in two Clean groups`);
      map.set(cls, group);
    }
  }
  return map;
}

/** The Clean group a primitive belongs to, or undefined if Clean never hides it. */
export function cleanGroupOf(primitive: object, map: CleanGroupMap): CleanTarget | undefined {
  // Exact class first, then walk up so a subclass inherits its parent's group.
  let proto: unknown = Object.getPrototypeOf(primitive);
  while (proto && proto !== Object.prototype) {
    const ctor = (proto as { constructor?: PrimitiveClass }).constructor;
    if (ctor) {
      const group = map.get(ctor);
      if (group) return group;
    }
    proto = Object.getPrototypeOf(proto);
  }
  return undefined;
}

export function makeCleanSuppressor(
  map: CleanGroupMap,
  getState: () => Pick<CleanChartState, 'active' | 'hides'> = useCleanChartStore.getState,
): PrimitiveSuppressor {
  return (primitive) => {
    const group = cleanGroupOf(primitive, map);
    return group !== undefined && isCleanHidden(getState(), group);
  };
}
